import React, { useMemo, useRef, useState } from 'react';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../api';
import { RichTextEditor, type RichTextEditorHandle } from '../../components/RichTextEditor';
import { useToast } from '../../components/ToastHost';
import { useAsync } from '../../hooks/useAsync';
import { fundraisingStageLabel } from '../../i18n';
import type { FundraisingStage } from '../../types';

type Recipient = {
  personId: string;
  personName: string;
  personEmail: string;
  personRole: string | null;
  isPrimary: 0 | 1;
  companyId: string;
  companyName: string;
  stage: string;
};

type SendResult = { personId: string; ok: boolean; resendId?: string | null; error?: string | null };
type SendResp = { ok: boolean; campaignId: string; results: SendResult[] };

type RecentEmail = {
  id: string;
  kind: 'link' | 'campaign';
  campaignId: string | null;
  toEmail: string;
  subject: string;
  status: 'sent' | 'failed';
  errorText: string | null;
  createdAt: string;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  repliedAt: string | null;
  bounceMessage: string | null;
  personId: string;
  personName: string;
  companyId: string;
  companyName: string;
};

type EmailTemplate = {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  stageOnSend: FundraisingStage | null;
  createdAt: string;
  updatedAt: string;
};

const STAGES: FundraisingStage[] = [
  'Mapeados',
  'Contactados',
  'Envío Info Principal',
  '1ra Reunión',
  'Interesados en continuar',
  'Envio Data Room',
  'En Negociación',
  'En Term Sheet',
  'Ganado',
  'Proceso Abandonado',
];

function norm(s: string) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function escapeHtml(s: string) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function decodeHtmlEntities(s: string) {
  return String(s || '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'");
}

function renderTemplate(template: string, vars: Record<string, string>) {
  return String(template || '').replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (m, key) => {
    const k = String(key || '').trim();
    if (Object.prototype.hasOwnProperty.call(vars, k)) return vars[k] ?? '';
    return m;
  });
}

function wrapEmailHtml(bodyHtml: string) {
  const body = String(bodyHtml || '').trim();
  return (
    `<div style="font-family:Montserrat,Segoe UI,Arial,sans-serif; color:#222; font-size:14px; line-height:1.55;">` +
    (body || '') +
    `</div>`
  );
}

function htmlToText(html: string) {
  const raw = String(html || '');
  const withBreaks = raw
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h1|h2|h3|h4|h5|h6)\s*>/gi, '\n\n')
    .replace(/<\s*li[^>]*>/gi, '- ')
    .replace(/<\s*\/\s*li\s*>/gi, '\n')
    .replace(/<\s*\/\s*(ul|ol)\s*>/gi, '\n');

  const stripped = withBreaks.replace(/<[^>]+>/g, '');
  const decoded = decodeHtmlEntities(stripped);
  return decoded
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function fmtTs(ts: string | null | undefined) {
  if (!ts) return '—';
  return ts.slice(0, 19).replace('T', ' ');
}

const TOKENS: Array<{ label: string; token: string }> = [
  { label: 'First name', token: '{{person_first_name}}' },
  { label: 'Full name', token: '{{person_name}}' },
  { label: 'Investor', token: '{{investor_name}}' },
  { label: 'Access link', token: '{{access_link}}' },
];

const STARTER_TEMPLATES: Array<{ name: string; subject: string; bodyHtml: string; stageOnSend?: FundraisingStage | null }> = [
  {
    name: 'Round update',
    subject: 'Quix — Seed round materials',
    stageOnSend: 'Contactados',
    bodyHtml:
      `<p style="margin:0 0 12px;">Hi {{person_first_name}},</p>` +
      `<p style="margin:0 0 12px;">Sharing Quix materials for our current round.</p>` +
      `<p style="margin:0 0 12px;">If you'd like, we can schedule a quick call to walk through traction, the metrics dashboard and the data room.</p>` +
      `<p style="margin:18px 0 18px;">` +
      `<a href="{{access_link}}" style="display:inline-block; padding:12px 18px; border-radius:999px; background:#00E7E4; color:#222; font-weight:800; text-decoration:none;">Open Investor Room</a>` +
      `</p>` +
      `<p style="margin:18px 0 0; font-size:12px; color:rgba(34,34,34,0.65);">Note: this content is confidential. Activity inside the platform is audited.</p>`,
  },
  {
    name: 'Follow-up',
    subject: 'Quix — Follow-up',
    bodyHtml:
      `<p style="margin:0 0 12px;">Hi {{person_first_name}},</p>` +
      `<p style="margin:0 0 12px;">Following up on our previous message.</p>` +
      `<p style="margin:0 0 12px;">Happy to answer any questions and share additional details.</p>` +
      `<p style="margin:18px 0 18px;">` +
      `<a href="{{access_link}}" style="display:inline-block; padding:12px 18px; border-radius:999px; background:#00E7E4; color:#222; font-weight:800; text-decoration:none;">Open Investor Room</a>` +
      `</p>` +
      `<p style="margin:18px 0 0; font-size:12px; color:rgba(34,34,34,0.65);">Note: this content is confidential. Activity inside the platform is audited.</p>`,
  },
  {
    name: 'Access link',
    subject: 'Quix Investor Room — Access',
    bodyHtml:
      `<p style="margin:0 0 12px;">Hi {{person_first_name}},</p>` +
      `<p style="margin:0 0 12px;">Here is your personal access link to review Quix materials.</p>` +
      `<p style="margin:18px 0 18px;">` +
      `<a href="{{access_link}}" style="display:inline-block; padding:12px 18px; border-radius:999px; background:#00E7E4; color:#222; font-weight:800; text-decoration:none;">Open Investor Room</a>` +
      `</p>` +
      `<p style="margin:18px 0 0; font-size:12px; color:rgba(34,34,34,0.65);">Note: this content is confidential. Activity inside the platform is audited.</p>`,
  },
];

export default function AdminEmails() {
  const toast = useToast();
  const { data, loading, error } = useAsync<{ recipients: Recipient[] }>(() => apiGet('/api/admin/recipients'), []);
  const recipients = data?.recipients || [];

  const {
    data: tmplData,
    loading: tmplLoading,
    error: tmplError,
    setData: setTmplData,
  } = useAsync<{ items: EmailTemplate[] }>(() => apiGet('/api/admin/email-templates?limit=200'), []);
  const templates = tmplData?.items || [];

  const refreshTemplates = async () => {
    const next = await apiGet<{ items: EmailTemplate[] }>('/api/admin/email-templates?limit=200');
    setTmplData(next);
  };

  const [query, setQuery] = useState('');
  const q = norm(query.trim());
  const [stageFilter, setStageFilter] = useState<string>('');
  const [onlyPrimary, setOnlyPrimary] = useState(false);

  const stageCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of recipients) m.set(r.stage, (m.get(r.stage) || 0) + 1);
    const stageOrder = new Map(STAGES.map((stage, index) => [stage, index]));
    return Array.from(m.entries()).sort((a, b) => {
      const aIndex = stageOrder.get(a[0] as FundraisingStage) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = stageOrder.get(b[0] as FundraisingStage) ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex || a[0].localeCompare(b[0]);
    });
  }, [recipients]);

  const filtered = useMemo(() => {
    return recipients.filter((r) => {
      if (onlyPrimary && !r.isPrimary) return false;
      if (stageFilter && r.stage !== stageFilter) return false;
      if (!q) return true;
      const hay = norm([r.companyName, r.personName, r.personEmail, r.personRole || '', r.stage].join(' '));
      return hay.includes(q);
    });
  }, [recipients, q, stageFilter, onlyPrimary]);

  const grouped = useMemo(() => {
    const by: Record<string, { companyId: string; companyName: string; stage: string; items: Recipient[] }> = {};
    for (const r of filtered) {
      if (!by[r.companyId]) by[r.companyId] = { companyId: r.companyId, companyName: r.companyName, stage: r.stage, items: [] };
      by[r.companyId]!.items.push(r);
    }
    return Object.values(by).sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [filtered]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (personId: string) => {
    setSelectedIds((prev) => {
      const set = new Set(prev);
      if (set.has(personId)) set.delete(personId);
      else set.add(personId);
      return Array.from(set);
    });
  };

  const selectAllFiltered = () => setSelectedIds(Array.from(new Set([...selectedIds, ...filtered.map((r) => r.personId)])));
  const selectPrimaryFiltered = () =>
    setSelectedIds(Array.from(new Set([...selectedIds, ...filtered.filter((r) => r.isPrimary).map((r) => r.personId)])));
  const clearSelection = () => setSelectedIds([]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [subject, setSubject] = useState('Quix — Seed round materials');
  const [bodyHtml, setBodyHtml] = useState<string>('');
  const [stageOnSend, setStageOnSend] = useState<FundraisingStage | ''>('');
  const editorRef = useRef<RichTextEditorHandle | null>(null);

  const selectedTemplate = useMemo(
    () => (selectedTemplateId ? templates.find((t) => t.id === selectedTemplateId) || null : null),
    [selectedTemplateId, templates]
  );

  const loadTemplate = (id: string) => {
    setSelectedTemplateId(id);
    if (!id) {
      setTemplateName('');
      setStageOnSend('');
      return;
    }
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setTemplateName(t.name);
    setSubject(t.subject);
    setBodyHtml(t.bodyHtml || '');
    setStageOnSend(t.stageOnSend || '');
  };

  const resetDraft = () => {
    setSelectedTemplateId('');
    setTemplateName('');
    setSubject('Quix — Seed round materials');
    setBodyHtml('');
    setStageOnSend('');
  };

  const [templateBusy, setTemplateBusy] = useState(false);

  const createStarterTemplates = async () => {
    setTemplateBusy(true);
    try {
      for (const t of STARTER_TEMPLATES) {
        await apiPost('/api/admin/email-templates', {
          name: t.name,
          subject: t.subject,
          bodyHtml: t.bodyHtml,
          stageOnSend: t.stageOnSend || null,
        });
      }
      await refreshTemplates();
      toast.success('Starter templates created.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not create templates.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) return toast.error('Template name is required.');
    if (!subject.trim()) return toast.error('Subject is required.');
    if (!bodyHtml.trim()) return toast.error('Email body is required.');
    setTemplateBusy(true);
    try {
      const resp = await apiPost<{ ok: boolean; id: string }>('/api/admin/email-templates', {
        name,
        subject: subject.trim(),
        bodyHtml: bodyHtml.trim(),
        bodyText: htmlToText(bodyHtml),
        stageOnSend: stageOnSend || null,
      });
      await refreshTemplates();
      setSelectedTemplateId(resp.id);
      toast.success('Template saved.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not save template.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const updateTemplate = async () => {
    if (!selectedTemplateId) return;
    const name = templateName.trim();
    if (!name) return toast.error('Template name is required.');
    if (!subject.trim()) return toast.error('Subject is required.');
    if (!bodyHtml.trim()) return toast.error('Email body is required.');
    setTemplateBusy(true);
    try {
      await apiPatch('/api/admin/email-templates/' + encodeURIComponent(selectedTemplateId), {
        name,
        subject: subject.trim(),
        bodyHtml: bodyHtml.trim(),
        bodyText: htmlToText(bodyHtml),
        stageOnSend: stageOnSend || null,
      });
      await refreshTemplates();
      toast.success('Template updated.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not update template.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplateId) return;
    if (!window.confirm('Delete this template?')) return;
    setTemplateBusy(true);
    try {
      await apiDelete('/api/admin/email-templates/' + encodeURIComponent(selectedTemplateId));
      setSelectedTemplateId('');
      setTemplateName('');
      await refreshTemplates();
      toast.success('Template deleted.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not delete template.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const insertGreeting = () => {
    editorRef.current?.insertHtml(`<p style="margin:0 0 12px;">Hi {{person_first_name}},</p>`);
  };

  const insertAccessButton = () => {
    editorRef.current?.insertHtml(
      `<p style="margin:18px 0 18px;">` +
        `<a href="{{access_link}}" style="display:inline-block; padding:12px 18px; border-radius:999px; background:#00E7E4; color:#222; font-weight:800; text-decoration:none;">Open Investor Room</a>` +
        `</p>`
    );
  };

  const insertConfidentialFooter = () => {
    editorRef.current?.insertHtml(
      `<p style="margin:18px 0 0; font-size:12px; color:rgba(34,34,34,0.65);">` +
        `Note: this content is confidential. Activity inside the platform is audited.` +
        `</p>`
    );
  };

  const [sending, setSending] = useState(false);
  const [sendResp, setSendResp] = useState<SendResp | null>(null);
  const [sendErr, setSendErr] = useState<string | null>(null);

  const previewVars = useMemo(
    () => ({
      person_first_name: 'Alex',
      person_name: 'Alex Johnson',
      investor_name: 'Example Ventures',
      access_link: 'https://example.com/l/your-link',
    }),
    []
  );

  const previewHtml = useMemo(() => {
    const varsHtml = {
      person_first_name: escapeHtml(previewVars.person_first_name),
      person_name: escapeHtml(previewVars.person_name),
      investor_name: escapeHtml(previewVars.investor_name),
      access_link: escapeHtml(previewVars.access_link),
    };
    return wrapEmailHtml(renderTemplate(bodyHtml, varsHtml));
  }, [bodyHtml, previewVars]);

  const effectiveHtml = useMemo(() => wrapEmailHtml(bodyHtml), [bodyHtml]);
  const effectiveText = useMemo(() => htmlToText(bodyHtml), [bodyHtml]);

  const canSend = selectedIds.length > 0 && subject.trim() && bodyHtml.trim() && !sending;
  const selectedCountLabel = `${selectedIds.length} recipient${selectedIds.length === 1 ? '' : 's'}`;

  const {
    data: recentData,
    loading: recentLoading,
    error: recentError,
    setData: setRecentData,
  } = useAsync<{ items: RecentEmail[] }>(() => apiGet('/api/admin/emails/recent?limit=50'), []);

  const refreshRecent = async () => {
    const next = await apiGet<{ items: RecentEmail[] }>('/api/admin/emails/recent?limit=50');
    setRecentData(next);
  };

  const onSend = async () => {
    setSending(true);
    setSendErr(null);
    setSendResp(null);
    try {
      const resp = await apiPost<SendResp>('/api/admin/emails/send', {
        subject,
        html: effectiveHtml,
        text: effectiveText,
        personIds: selectedIds,
        stageOnSend: stageOnSend || null,
      });
      setSendResp(resp);
      const okCount = (resp.results || []).filter((r) => r.ok).length;
      toast.success(`Sent ${okCount} email${okCount === 1 ? '' : 's'}.`);
      refreshRecent().catch(() => {});
    } catch (e: any) {
      const msg = e?.payload?.error || e?.message || 'Could not send.';
      setSendErr(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Emails</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Send individual emails (1 per person). Clicks are tracked automatically.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="pill">{selectedCountLabel}</div>
            <button className="btn" onClick={selectAllFiltered} disabled={!filtered.length}>
              Select all filtered
            </button>
            <button className="btn" onClick={selectPrimaryFiltered} disabled={!filtered.some((r) => r.isPrimary)}>
              Select primary filtered
            </button>
            <button className="btn danger" onClick={clearSelection} disabled={!selectedIds.length}>
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(320px, 1fr) 1.1fr', gap: 14 }}>
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Recipients</div>
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Stage
              </div>
              <select className="select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                <option value="">All stages ({recipients.length})</option>
                {stageCounts.map(([stage, count]) => (
                  <option key={stage} value={stage}>
                    {fundraisingStageLabel(stage as any)} ({count})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Search
              </div>
              <input
                className="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search investor / person / email / stage…"
              />
            </div>
          </div>
          <div style={{ height: 10 }} />
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="checkbox" checked={onlyPrimary} onChange={(e) => setOnlyPrimary(e.target.checked)} />
            <span className="muted" style={{ fontSize: 12 }}>
              Only primary contacts
            </span>
          </label>

          <div style={{ height: 12 }} />
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="muted">Error: {error}</div>
          ) : !grouped.length ? (
            <div className="muted">No recipients.</div>
          ) : (
            <div className="grid" style={{ gap: 12 }}>
              {grouped.map((g) => (
                <div key={g.companyId} style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
                    <div style={{ fontWeight: 850, display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span>{g.companyName}</span>
                      <span className="pill">
                        {g.items.filter((x) => selectedSet.has(x.personId)).length}/{g.items.length} selected
                      </span>
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {fundraisingStageLabel(g.stage as any)}
                    </div>
                  </div>
                  <div style={{ height: 8 }} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <button
                      className="btn"
                      onClick={() => {
                        setSelectedIds((prev) => {
                          const set = new Set(prev);
                          const allSelected = g.items.every((x) => set.has(x.personId));
                          if (allSelected) g.items.forEach((x) => set.delete(x.personId));
                          else g.items.forEach((x) => set.add(x.personId));
                          return Array.from(set);
                        });
                      }}
                    >
                      {g.items.every((x) => selectedSet.has(x.personId)) ? 'Unselect investor' : 'Select investor'}
                    </button>
                  </div>
                  <div className="grid" style={{ gap: 8 }}>
                    {g.items.map((r) => {
                      const checked = selectedSet.has(r.personId);
                      return (
                        <label key={r.personId} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggle(r.personId)} style={{ marginTop: 3 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                              <div style={{ fontWeight: 750 }}>
                                {r.personName}
                                {r.isPrimary ? (
                                  <span className="pill" style={{ marginLeft: 8 }}>
                                    Primary
                                  </span>
                                ) : null}
                              </div>
                              <div className="muted" style={{ fontSize: 12 }}>
                                {r.personRole || '—'}
                              </div>
                            </div>
                            <div className="muted" style={{ fontSize: 12 }}>
                              {r.personEmail}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid" style={{ gap: 14 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>Compose</div>
              <button className="btn" onClick={resetDraft} type="button">
                New draft
              </button>
            </div>

            <div style={{ height: 10 }} />
            {tmplLoading ? (
              <div className="muted">Loading templates…</div>
            ) : tmplError ? (
              <div className="muted">Templates error: {tmplError}</div>
            ) : !templates.length ? (
              <div className="banner" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  No templates yet. Create starter templates to get started.
                </div>
                <button className="btn" onClick={createStarterTemplates} disabled={templateBusy}>
                  {templateBusy ? 'Creating…' : 'Create starter templates'}
                </button>
              </div>
            ) : null}

            <div style={{ height: 10 }} />
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  Template
                </div>
                <select
                  className="select"
                  value={selectedTemplateId}
                  onChange={(e) => loadTemplate(e.target.value)}
                  disabled={tmplLoading || templateBusy}
                >
                  <option value="">Draft (no template)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  Template name
                </div>
                <input
                  className="input"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Ex: Round update"
                  disabled={templateBusy}
                />
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  After send
                </div>
                <select className="select" value={stageOnSend} onChange={(e) => setStageOnSend(e.target.value as FundraisingStage | '')}>
                  <option value="">Do not move stage</option>
                  {STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      Move to {fundraisingStageLabel(stage)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: 10 }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedTemplateId ? (
                <>
                  <button className="btn" onClick={updateTemplate} disabled={templateBusy}>
                    {templateBusy ? 'Saving…' : 'Update template'}
                  </button>
                  <button className="btn danger" onClick={deleteTemplate} disabled={templateBusy}>
                    Delete template
                  </button>
                  {selectedTemplate ? (
                    <div className="muted" style={{ fontSize: 12 }}>
                      Updated: {fmtTs(selectedTemplate.updatedAt)}
                    </div>
                  ) : null}
                </>
              ) : (
                <button className="btn" onClick={saveAsTemplate} disabled={templateBusy}>
                  {templateBusy ? 'Saving…' : 'Save as template'}
                </button>
              )}
            </div>

            <div style={{ height: 12 }} />
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Subject
            </div>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex: Quix — Round update" />

            <div style={{ height: 12 }} />
            <div className="banner" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                Insert
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn" type="button" onClick={insertGreeting}>
                  Greeting
                </button>
                <button className="btn" type="button" onClick={insertAccessButton}>
                  Access button
                </button>
                <button className="btn" type="button" onClick={insertConfidentialFooter}>
                  Confidentiality footer
                </button>
                <div style={{ flexBasis: '100%', height: 0 }} />
                <div className="muted" style={{ fontSize: 12 }}>
                  Tokens:
                </div>
                {TOKENS.map((t) => (
                  <button
                    key={t.token}
                    type="button"
                    className="token-btn"
                    onClick={() => editorRef.current?.insertText(t.token)}
                    title={t.label}
                  >
                    {t.token}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ height: 12 }} />
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Body
            </div>
            <RichTextEditor ref={editorRef} valueHtml={bodyHtml} onChangeHtml={setBodyHtml} placeholder="Write your email…" minHeight={260} />

            <div style={{ height: 12 }} />
            <div className="card" style={{ padding: 12, margin: 0, borderRadius: 16, boxShadow: 'none', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 900 }}>Preview</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Example recipient
                </div>
              </div>
              <div style={{ height: 10 }} />
              <iframe
                title="Email preview"
                sandbox=""
                style={{ width: '100%', height: 240, border: '1px solid var(--border)', borderRadius: 12, background: '#fff' }}
                srcDoc={previewHtml}
              />
            </div>

            <div style={{ height: 12 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Sends 1 email per person. A CRM note is added automatically on the investor.
              </div>
              <button className="btn btn--primary" onClick={onSend} disabled={!canSend}>
                {sending ? 'Sending…' : `Send (${selectedCountLabel})`}
              </button>
            </div>
          </div>

          <div className="card">
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Result</div>
            {sendErr ? <div className="muted">Error: {sendErr}</div> : null}
            {!sendResp ? (
              <div className="muted">—</div>
            ) : (
              <>
                <div className="muted" style={{ fontSize: 12 }}>
                  Campaign ID: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{sendResp.campaignId}</span>
                </div>
                <div style={{ height: 10 }} />
                <table className="table">
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Status</th>
                      <th>Resend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sendResp.results.map((r) => {
                      const rec = recipients.find((x) => x.personId === r.personId);
                      return (
                        <tr key={r.personId}>
                          <td>
                            <div style={{ fontWeight: 800 }}>{rec?.personName || r.personId}</div>
                            <div className="muted" style={{ fontSize: 12 }}>
                              {rec?.companyName || '—'} · {rec?.personEmail || '—'}
                            </div>
                          </td>
                          <td className="muted">{r.ok ? 'Sent' : `Failed${r.error ? `: ${r.error}` : ''}`}</td>
                          <td className="muted">{r.resendId || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>Recent sends</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Last 50 emails (campaign + link)
                </div>
              </div>
              <button className="btn" onClick={refreshRecent} disabled={recentLoading}>
                Refresh
              </button>
            </div>
            <div style={{ height: 12 }} />
            {recentLoading ? (
              <div className="muted">Loading…</div>
            ) : recentError ? (
              <div className="muted">Error: {recentError}</div>
            ) : !recentData?.items?.length ? (
              <div className="muted">—</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Investor</th>
                    <th>Person</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {(recentData.items || []).slice(0, 50).map((e) => (
                    <tr key={e.id}>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                        {fmtTs(e.createdAt)}
                      </td>
                      <td>
                        <div style={{ fontWeight: 800 }}>{e.companyName}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {e.kind === 'campaign' ? `Campaign ${e.campaignId?.slice(0, 8) || '—'}` : 'Link'}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 800 }}>{e.personName}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {e.toEmail}
                        </div>
                      </td>
                      <td className="muted">{e.subject}</td>
                      <td className="muted">{e.status === 'sent' ? 'Sent' : `Failed${e.errorText ? `: ${e.errorText}` : ''}`}</td>
                      <td className="muted">
                        {e.bouncedAt
                          ? `Bounced${e.bounceMessage ? `: ${e.bounceMessage}` : ''}`
                          : e.repliedAt
                            ? `Replied ${fmtTs(e.repliedAt)}`
                            : e.openedAt
                              ? `Opened ${fmtTs(e.openedAt)}`
                              : e.clickedAt
                                ? `Clicked ${fmtTs(e.clickedAt)}`
                                : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
